/** Pure lap-clock alignment shared by web and native trace views. */
export type TraceAlignmentMode = 'time' | 'gps';

export type TraceAlignmentPoint = {
  timecodeMs: number;
  lat: number;
  lon: number;
  x: number;
  y: number;
};

/** Only measured path samples and the source lap window are needed. */
export type TraceAlignmentLayer = {
  id: string;
  analysis: {
    startMs: number;
    endMs: number;
    durationSec: number;
  };
  points: readonly TraceAlignmentPoint[];
};

type TraceAlignmentAnchor = {
  elapsedSec: number;
  progress: number;
};

export type TraceAlignmentMapper = {
  toAlignedSec: (sourceElapsedSec: number) => number;
  fromAlignedSec: (alignedElapsedSec: number) => number;
};

function interpolateAlignmentAnchors(
  anchors: TraceAlignmentAnchor[],
  value: number,
  input: 'elapsedSec' | 'progress',
  output: 'elapsedSec' | 'progress',
) {
  if (anchors.length === 0 || !Number.isFinite(value)) return 0;
  if (value <= anchors[0]![input]) return anchors[0]![output];
  if (value >= anchors.at(-1)![input]) return anchors.at(-1)![output];

  let low = 0;
  let high = anchors.length - 1;
  while (low < high) {
    const mid = Math.floor((low + high) / 2);
    if (anchors[mid]![input] < value) low = mid + 1;
    else high = mid;
  }

  const next = anchors[low]!;
  const prev = anchors[Math.max(0, low - 1)]!;
  const span = next[input] - prev[input];
  if (span <= 0) return next[output];
  const ratio = Math.min(1, Math.max(0, (value - prev[input]) / span));
  return prev[output] + (next[output] - prev[output]) * ratio;
}

function gpsSegmentDistance(
  previous: TraceAlignmentLayer['points'][number],
  next: TraceAlignmentLayer['points'][number],
) {
  if (
    Number.isFinite(previous.lat) &&
    Number.isFinite(previous.lon) &&
    Number.isFinite(next.lat) &&
    Number.isFinite(next.lon)
  ) {
    const meanLatitudeRadians = (((previous.lat + next.lat) / 2) * Math.PI) / 180;
    const latitudeDelta = next.lat - previous.lat;
    const longitudeDelta = (next.lon - previous.lon) * Math.cos(meanLatitudeRadians);
    return Math.hypot(latitudeDelta, longitudeDelta);
  }
  return Math.hypot(next.x - previous.x, next.y - previous.y);
}

function buildGpsAlignmentAnchors(layer: TraceAlignmentLayer): TraceAlignmentAnchor[] {
  const durationSec = Math.max(0, layer.analysis.durationSec);
  const points = layer.points
    .filter(
      (point) =>
        point.timecodeMs >= layer.analysis.startMs && point.timecodeMs <= layer.analysis.endMs,
    )
    .sort((left, right) => left.timecodeMs - right.timecodeMs);

  if (points.length < 2 || durationSec <= 0) {
    return [
      { elapsedSec: 0, progress: 0 },
      { elapsedSec: durationSec, progress: 1 },
    ];
  }

  const distances = [0];
  for (let index = 1; index < points.length; index += 1) {
    distances.push(distances[index - 1]! + gpsSegmentDistance(points[index - 1]!, points[index]!));
  }
  const totalDistance = distances.at(-1) ?? 0;
  if (!Number.isFinite(totalDistance) || totalDistance <= 0) {
    return [
      { elapsedSec: 0, progress: 0 },
      { elapsedSec: durationSec, progress: 1 },
    ];
  }

  const anchors = points.map((point, index) => ({
    elapsedSec: Math.min(
      durationSec,
      Math.max(0, (point.timecodeMs - layer.analysis.startMs) / 1000),
    ),
    progress: distances[index]! / totalDistance,
  }));
  if (anchors[0]!.elapsedSec > 0) anchors.unshift({ elapsedSec: 0, progress: 0 });
  if (anchors.at(-1)!.elapsedSec < durationSec)
    anchors.push({ elapsedSec: durationSec, progress: 1 });
  return anchors;
}

/** Project onto one shared path; independent lap lengths do not define position. */
function buildProjectedAlignmentAnchors(
  layer: TraceAlignmentLayer,
  primary: TraceAlignmentLayer,
  primaryAnchors: TraceAlignmentAnchor[],
): TraceAlignmentAnchor[] {
  const samplePath = (source: TraceAlignmentLayer, limit: number) => {
    const points = source.points
      .filter(
        (point) =>
          point.timecodeMs >= source.analysis.startMs &&
          point.timecodeMs <= source.analysis.endMs &&
          Number.isFinite(point.lat) &&
          Number.isFinite(point.lon),
      )
      .sort((a, b) => a.timecodeMs - b.timecodeMs);
    const stride = Math.max(1, Math.ceil((points.length - 1) / (limit - 1)));
    return points.filter((_point, index) => index % stride === 0 || index === points.length - 1);
  };
  const reference = samplePath(primary, 1024);
  const samples = samplePath(layer, 2048);
  if (reference.length < 2 || samples.length < 2) return [];
  const longitudeScale = Math.cos((reference[0]!.lat * Math.PI) / 180);
  const origin = reference[0]!;
  const path = reference.map((point) => ({
    x: (point.lon - origin.lon) * longitudeScale,
    y: point.lat - origin.lat,
    progress: interpolateAlignmentAnchors(
      primaryAnchors,
      (point.timecodeMs - primary.analysis.startMs) / 1000,
      'elapsedSec',
      'progress',
    ),
  }));
  const referenceLength = path
    .slice(1)
    .reduce(
      (sum, point, index) => sum + Math.hypot(point.x - path[index]!.x, point.y - path[index]!.y),
      0,
    );
  let previousProgress = 0;
  let previousElapsedSec = 0;
  const anchors = samples.map((point, sampleIndex) => {
    const x = (point.lon - origin.lon) * longitudeScale;
    const y = point.lat - origin.lat;
    const elapsedSec = (point.timecodeMs - layer.analysis.startMs) / 1000;
    // Sampling gaps widen the neighborhood; spatial outliers alone cannot skip
    // an unrelated section. A fourfold lap-average speed allowance stays local.
    const forwardWindow = Math.max(
      0.005,
      (4 * Math.max(0, elapsedSec - previousElapsedSec)) / Math.max(1, layer.analysis.durationSec),
    );
    const maximumProgress = Math.min(1, previousProgress + forwardWindow);
    let bestDistance = Infinity;
    let progress = previousProgress;
    for (let index = 1; index < path.length; index += 1) {
      const a = path[index - 1]!;
      const b = path[index]!;
      const span = b.progress - a.progress;
      if (b.progress < previousProgress || a.progress > maximumProgress || span <= 0) continue;
      const dx = b.x - a.x;
      const dy = b.y - a.y;
      const lengthSquared = dx * dx + dy * dy;
      if (lengthSquared <= 0) continue;
      const minRatio = Math.max(0, (previousProgress - a.progress) / span);
      const maxRatio = Math.min(1, (maximumProgress - a.progress) / span);
      const ratio = Math.max(
        minRatio,
        Math.min(maxRatio, ((x - a.x) * dx + (y - a.y) * dy) / lengthSquared),
      );
      const jump = a.progress + ratio * span - previousProgress;
      const distance =
        (x - a.x - ratio * dx) ** 2 +
        (y - a.y - ratio * dy) ** 2 +
        (jump * referenceLength) ** 2 * 0.0001;
      if (distance < bestDistance) {
        bestDistance = distance;
        progress = a.progress + ratio * span;
      }
    }
    if (sampleIndex === 0) progress = 0;
    if (sampleIndex === samples.length - 1) progress = 1;
    previousProgress = progress;
    previousElapsedSec = elapsedSec;
    return { elapsedSec, progress };
  });
  if (anchors[0]!.elapsedSec > 0) anchors.unshift({ elapsedSec: 0, progress: 0 });
  if (anchors.at(-1)!.elapsedSec < layer.analysis.durationSec) {
    anchors.push({ elapsedSec: layer.analysis.durationSec, progress: 1 });
  }
  return anchors;
}

/**
 * Maps every compared lap onto the primary lap's GPS progress. The aligned x-axis
 * remains the primary lap's clock, so seeking and zooming retain one reference.
 */
export function buildTraceAlignmentMappers(
  layers: readonly TraceAlignmentLayer[],
  mode: TraceAlignmentMode,
): Map<string, TraceAlignmentMapper> {
  const primary = layers.find((layer) => layer.id === 'primary');
  if (mode === 'time' || !primary || layers.some((layer) => layer.points.length < 2)) {
    return new Map(
      layers.map((layer) => [
        layer.id,
        {
          toAlignedSec: (elapsedSec: number) => elapsedSec,
          fromAlignedSec: (elapsedSec: number) => elapsedSec,
        },
      ]),
    );
  }

  const primaryAnchors = buildGpsAlignmentAnchors(primary);
  return new Map(
    layers.map((layer) => {
      const layerAnchors =
        layer.id === 'primary'
          ? primaryAnchors
          : buildProjectedAlignmentAnchors(layer, primary, primaryAnchors);
      return [
        layer.id,
        {
          toAlignedSec: (sourceElapsedSec: number) => {
            const progress = interpolateAlignmentAnchors(
              layerAnchors,
              sourceElapsedSec,
              'elapsedSec',
              'progress',
            );
            return interpolateAlignmentAnchors(primaryAnchors, progress, 'progress', 'elapsedSec');
          },
          fromAlignedSec: (alignedElapsedSec: number) => {
            const progress = interpolateAlignmentAnchors(
              primaryAnchors,
              alignedElapsedSec,
              'elapsedSec',
              'progress',
            );
            return interpolateAlignmentAnchors(layerAnchors, progress, 'progress', 'elapsedSec');
          },
        },
      ];
    }),
  );
}
